export interface Reminder {
  id: string
  text: string
  dueAt: string
  status: 'open' | 'done'
}

export type ReminderCommand
  = { action: 'list' }
    | { action: 'create', id: string, text: string, dueAt: string }
    | { action: 'complete', id: string }

export interface ReminderOutcome {
  reminders: Reminder[]
  status: 'listed' | 'saved' | 'completed' | 'INVALID_INPUT'
  reminder?: Reminder
  message: string
}

function reminderId(id: string) {
  const trimmed = id.trim()
  if (!trimmed || trimmed.length > 80 || /[\r\n]/.test(trimmed))
    return undefined
  return trimmed
}

function reminderText(text: string) {
  const trimmed = text.trim()
  if (!trimmed || trimmed.length > 200)
    return undefined
  return trimmed
}

function dueAt(value: string) {
  const trimmed = value.trim()
  if (!Number.isFinite(Date.parse(trimmed)))
    return undefined
  return new Date(trimmed).toISOString()
}

/**
 * Creates, completes, or lists reminders.
 * Creating the same id again returns the existing reminder and does not add a second row.
 *
 * @example
 * applyReminder([], { action: 'create', id: 'reminder:check', text: 'Check Velis', dueAt: '2026-10-05T06:00:00.000Z' })
 * // => { status: 'saved', reminder: { id: 'reminder:check', status: 'open', ... }, reminders: [...] }
 */
export function applyReminder(reminders: readonly Reminder[], command: ReminderCommand): ReminderOutcome {
  if (command.action === 'list') {
    return {
      reminders: [...reminders],
      status: 'listed',
      message: `${reminders.length} reminder${reminders.length === 1 ? '' : 's'}.`,
    }
  }

  const id = reminderId(command.id)
  if (!id)
    return { reminders: [...reminders], status: 'INVALID_INPUT', message: 'A reminder needs a stable id.' }

  const current = reminders.find(reminder => reminder.id === id)
  if (command.action === 'complete') {
    if (!current)
      return { reminders: [...reminders], status: 'INVALID_INPUT', message: 'That reminder was not found.' }
    if (current.status === 'done')
      return { reminders: [...reminders], status: 'completed', reminder: current, message: 'The reminder was already complete.' }
    const completed = { ...current, status: 'done' as const }
    return {
      reminders: reminders.map(reminder => reminder.id === id ? completed : reminder),
      status: 'completed',
      reminder: completed,
      message: 'The reminder is complete.',
    }
  }

  const text = reminderText(command.text)
  const due = dueAt(command.dueAt)
  if (!text || !due)
    return { reminders: [...reminders], status: 'INVALID_INPUT', message: 'A reminder needs text and a due time.' }
  if (current)
    return { reminders: [...reminders], status: 'saved', reminder: current, message: 'The reminder already exists.' }

  const reminder = { id, text, dueAt: due, status: 'open' as const }
  return {
    reminders: [...reminders, reminder],
    status: 'saved',
    reminder,
    message: 'The reminder was saved on this computer.',
  }
}
